
import PropTypes from 'prop-types'

/**
 * FaultAlert Component
 * 
 * Displays a red alert banner when faults are detected, matching the original HTML dashboard.
 * Shows/hides automatically based on fault presence with smooth transitions.
 */
function FaultAlert({ faults, isVisible }) {
  if (!isVisible || !faults || faults.length === 0) {
    return null
  }

  return (
    <div
      className="card shadow-sm border-0 mb-3 bg-danger text-white"
      style={{ 
        display: 'block',
        background: 'linear-gradient(to right, #dc3545, #c82333)',
        transition: 'all 0.3s ease',
      }}
      role="alert"
      aria-live="polite"
      data-testid="fault-alert"
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-1px)'
        e.currentTarget.style.boxShadow = '0 8px 25px rgba(220,53,69,0.3)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)'
        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)'
      }}
    >
      <div className="card-header fw-bold border-0 d-flex align-items-center" style={{ backgroundColor: 'transparent' }}>
        <i className="bi bi-exclamation-triangle fs-4 me-2" aria-hidden="true"></i>
        <span className="canbus-connected-text">
          {faults.length === 1 ? 'Fault detected' : 'Faults detected'}
        </span>
        <span 
          className="badge bg-white text-dark ms-2 align-middle"
          style={{
            transition: 'all 0.2s ease',
          }}
        >
          {faults.length}
        </span>
      </div>
      <div className="card-body" id="fault-details" style={{ backgroundColor: 'transparent' }}>
        {faults.map((fault, index) => (
          <div key={`${fault.device}-${fault.measurement}-${fault.time || index}`} className="mb-1">
            <i className="bi bi-dot" aria-hidden="true"></i>
            <strong>{fault.device}</strong> {fault.measurement} {fault.value}
            {fault.description && (
              <span className="text-white-50 ms-1">({fault.description})</span>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

FaultAlert.propTypes = {
  /** Array of fault objects to display */
  faults: PropTypes.arrayOf(
    PropTypes.shape({
      device: PropTypes.string.isRequired,
      measurement: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      description: PropTypes.string,
      time: PropTypes.string,
    }),
  ),
  /** Controls visibility of the alert banner */
  isVisible: PropTypes.bool,
}

FaultAlert.defaultProps = {
  faults: [],
  isVisible: false,
}

export default FaultAlert
